/*
 * Server error shapes and retry policy.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS FILE EXISTS (PF-13)
 * ---------------------------------------------------------------------------
 * `POST /orderDeliverTo` used to return **201 for every outcome**, including
 * validation failures, which were delivered as an error body under a "created"
 * status. Our services `map`ped that body into a model object, which is truthy,
 * so `if (created) { ... }` passed and the caller went on to do the success
 * thing -- in the cart's case, clearing the customer's cart on a FAILED order.
 *
 * The server fixed the status codes on 2026-08-27. Two client-side problems
 * survived that fix and are what this module addresses:
 *
 *   1. `retry(6)` on a POST retried EVERYTHING, so a 400 "your quantities are
 *      invalid" was re-sent seven times. Retrying a deterministic rejection
 *      cannot succeed; it only multiplies load and delays the error the user
 *      needs to see.
 *   2. The failure paths only `console.log`ed, so a rejected order looked
 *      exactly like nothing happening.
 *
 * ---------------------------------------------------------------------------
 * TWO ERROR SHAPES IN ONE API -- THIS IS NOT A MISTAKE, IT IS THE CONTRACT
 * ---------------------------------------------------------------------------
 * Confirmed by the server in
 * `docs/contracts/inbox/2026-08-27-folder-forensics-and-error-shape-correction.md`:
 *
 *   SmartError       { statusCode, message, details }   <- orderDeliverTo, most
 *                                                          non-Project routes
 *   ResponseMessage  { message, status, data }          <- Project routes
 *
 * They collide on the key `message`, and they mean OPPOSITE things by it:
 *
 *   - SmartError.message is a SHORT STABLE CODE for branching. Current values
 *     are `request error`, `not found`, `manufacturing set closed`. Showing it
 *     to a user produces an alert box reading "request error", which is worse
 *     than useless -- it looks like a bug in our UI. The human sentence is in
 *     `details`.
 *   - ResponseMessage.message IS the human sentence; there is no `details`.
 *
 * So "just display `message`" is right for one shape and wrong for the other,
 * which is exactly the trap the server wrote to warn us about. The
 * discriminator used below is the presence of `statusCode`: only SmartError
 * carries it. Branch on {@link serverErrorCode}, display
 * {@link describeServerError}.
 *
 * ---------------------------------------------------------------------------
 * A THIRD SHAPE, ADDED 2026-09-16 -- `{ error, details? }`
 * ---------------------------------------------------------------------------
 * Per `docs/contracts/inbox/_processed/2026-09-16c-error-responses-and-crash-containment.md`
 * and `shared/ErrorResponses.md`. This is what the Express error middleware now
 * returns for anything that *threw*, as opposed to a deliberate refusal:
 *
 *   ErrorMiddleware  { error, details? }   <- any unhandled route error
 *
 * The prose is under `error`, and `details` is present only when the thrown
 * error carried one. So a body can be `{ error: "..." }` alone, and reading only
 * `details`/`message` discards the sentence and shows our fallback instead of
 * what the server actually said. The server's own summary is that
 * `body.message || body.error` covers both shapes; the order below is
 * `details` -> `message` -> `error` because `details` is the most specific and
 * SmartError's `message` must still never be displayed.
 *
 * Note this middleware body has **no `statusCode`**, so it is not mistaken for a
 * SmartError and its `error` is never treated as a machine code.
 *
 * Before that rewrite the same middleware called `res.send('error', {...})` --
 * `res.render()` syntax -- so the object was dropped and the body was the six
 * literal bytes `error`. {@link isPlaceholderErrorText} keeps a stray one off
 * the screen: displayed verbatim it reads as a bug in our UI, not as a server
 * message.
 *
 * ---------------------------------------------------------------------------
 * NO ANGULAR IMPORTS -- DELIBERATE
 * ---------------------------------------------------------------------------
 * `@angular/core` is ESM-only from Angular 16 and cannot be `require()`d by
 * ts-node, so anything importing it is unreachable from the layer-0 smoke
 * tests. `HttpErrorResponse` is therefore matched structurally rather than
 * imported. Same reasoning as `service-data/project-endpoints.ts`.
 * Covered by `tests/HttpErrorPolicy.smoke.ts`.
 */
import { MonoTypeOperatorFunction, Observable, throwError, timer } from 'rxjs';
import { concatMap, retryWhen } from 'rxjs/operators';

/** Normalized view of a failed (or falsely-successful) server call. */
export interface ServerErrorInfo {
  /** Transport-level HTTP status. 0 when the request never got a response. */
  httpStatus: number;
  /** `SmartError.statusCode` when present, otherwise {@link httpStatus}. */
  statusCode: number;
  /**
   * Stable machine code for branching -- `SmartError.message`. Empty string
   * when the body was not a SmartError. NEVER display this.
   */
  code: string;
  /** Human sentence, if the server supplied one. May be empty. */
  details: string;
  /** The parsed body, for logging. */
  body: any;
  /** The original error object, for logging. */
  raw: any;
}

/** An `Error` carrying the normalized server information. */
export type ServerCallError = Error & ServerErrorInfo & { operation: string };

function asString(value: any): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asNumber(value: any): number {
  return typeof value === 'number' && isFinite(value) ? value : NaN;
}

/**
 * True when a candidate "sentence" carries no information and must not be
 * shown to a user.
 *
 * Until the server's 2026-09-16 rewrite the Express error middleware emitted
 * the six literal bytes `error` as the entire body. Passed through as prose it
 * produces an alert box reading "error", which is indistinguishable from a
 * defect in this client. Empty and whitespace-only are the same case.
 */
function isPlaceholderErrorText(text: string): boolean {
  const normalized = text.trim().toLowerCase();
  return normalized === '' || normalized === 'error';
}

/**
 * True when a string body is markup rather than a sentence.
 *
 * ---------------------------------------------------------------------------
 * WHY (observed 2026-10-23)
 * ---------------------------------------------------------------------------
 * An Express app that matches no route answers with an HTML error page, not
 * JSON. API Gateway and CloudFront do the same for their own 4xx/5xx, and S3
 * answers in XML. Angular hands that entire document over as
 * `HttpErrorResponse.error`, and until this guard existed `extractBody`
 * wrapped it as `{ details: text }` -- so `describeServerError` treated a
 * whole web page as the server's human sentence and put it in a snack-bar:
 *
 *     <!DOCTYPE html> <html lang="en"> <head> ... <pre>Cannot POST
 *     /1.0/ui/project/mapping/plan/12256</pre> ...
 *
 * That is worse than showing nothing: the caller's already-translated
 * fallback was suppressed in favour of untranslated markup, so the one
 * message written for a human never appeared. It affected every caller of
 * `describeServerError`, not the route that happened to surface it -- any
 * un-deployed endpoint, any gateway 502, any typo in a path.
 *
 * The markup is NOT discarded: it stays on `ServerErrorInfo.body` so the
 * console log still carries the whole page. It is barred from being
 * *displayed*, which is a different thing.
 */
function isMarkup(text: string): boolean {
  return text.charAt(0) === '<';
}

/**
 * Pull the body out of whatever the HttpClient handed us.
 *
 * `HttpErrorResponse.error` is the parsed body when the response carried JSON,
 * the raw text when it did not, and a `ProgressEvent` when the request never
 * reached the server at all. A JSON body served as `text/plain` arrives as a
 * string, so a parse is attempted before giving up.
 */
function extractBody(error: any): any {
  if (!error || typeof error !== 'object') { return error; }
  let body = ('error' in error) ? (error as any).error : error;
  if (typeof body === 'string') {
    const text = body.trim();
    if (text.charAt(0) === '{' || text.charAt(0) === '[') {
      try { return JSON.parse(text); } catch (e) { /* not JSON; fall through */ }
    }
    if (isMarkup(text)) {
      // Keyed as `markup` deliberately: it is not `details`, not `message`
      // and not `error`, so no display path can reach it, while the logging
      // path -- which prints the whole body -- still does.
      return { markup: text };
    }
    return { details: text };
  }
  // A ProgressEvent (network failure) carries nothing usable.
  if (body && typeof body === 'object' && typeof (body as any).type === 'string'
      && !('message' in body) && !('details' in body) && !('statusCode' in body)) {
    return undefined;
  }
  return body;
}

/** Normalize any thrown value into a {@link ServerErrorInfo}. */
export function parseServerError(error: any): ServerErrorInfo {
  const httpStatus = asNumber(error && error.status);
  const body = extractBody(error);
  const smartStatus = asNumber(body && body.statusCode);

  const isSmartError = !isNaN(smartStatus);
  const code = isSmartError ? asString(body.message) : '';

  let details = asString(body && body.details);
  if (!details && !isSmartError) {
    // ResponseMessage `{message, status, data}` -- here `message` IS the prose.
    // Also covers a bare `{message}` and an Error instance.
    details = asString(body && body.message);
  }
  if (!details) {
    // Express error-middleware `{error, details?}` (server, 2026-09-16). The
    // prose is under `error` and `details` only appears when the thrown error
    // carried one, so without this the server's sentence is silently discarded
    // and the caller shows its own fallback instead.
    details = asString(body && body.error);
  }
  if (isPlaceholderErrorText(details)) {
    details = '';
  }

  return {
    httpStatus: isNaN(httpStatus) ? 0 : httpStatus,
    statusCode: isSmartError ? smartStatus : (isNaN(httpStatus) ? 0 : httpStatus),
    code: code,
    details: details,
    body: body,
    raw: error
  };
}

/** The stable machine code, for branching. `''` when there is not one. */
export function serverErrorCode(error: any): string {
  return parseServerError(error).code;
}

/** What a 429 told us about the limit that was hit. */
export interface RateLimitInfo {
  /** Calls allowed per window. `NaN` when the server did not say. */
  limit: number;
  /**
   * Length of the rolling window in seconds -- the RULE, currently 3600.
   *
   * ⚠ **This is not a retry time and must never be shown as one.** The window
   * rolls, so the allowance returns as each ticket ages out, which is usually
   * far sooner than the window length. Use {@link retryAfterSeconds}.
   */
  windowSeconds: number;
  /**
   * When the limiter will actually admit another call, in seconds.
   *
   * `NaN` when the server did not say -- and in that case the caller must NOT
   * substitute {@link windowSeconds}. Saying "try again in an hour" when the
   * true wait is three minutes is the exact defect the server fixed on
   * 2026-09-14; falling back to the window would reintroduce it client-side.
   */
  retryAfterSeconds: number;
  /** The server's own English sentence, for logging. May be empty. */
  message: string;
}

/**
 * Read the rate-limit envelope off a 429.
 *
 * `POST /resource/upload` answers
 * `{ error, limit, retryAfterSeconds, windowSeconds }` with a `Retry-After`
 * header (`docs/contracts/shared/ResourceUploads.md` §3). Note the prose is
 * under `error`, which is neither of the two documented body shapes, so
 * `describeServerError` does not find it -- that is deliberate here, because
 * the sentence we show is built and translated by the caller.
 *
 * **The BODY is preferred over the header, not the other way round.** A header
 * is only readable cross-origin when the server lists it in
 * `Access-Control-Expose-Headers`; if it does not, `headers.get('Retry-After')`
 * returns null and we would silently lose the number. The body always crosses.
 * The two agree when both are present.
 *
 * **Read `limit` from the body. Never hardcode it.** It is environment
 * configuration (`ResourceUploadUrlsPerOrgPerHour`) and the server expects to
 * raise it per customer; a literal in the client would then tell the user a
 * number that is no longer true.
 */
export function parseRateLimit(error: any): RateLimitInfo {
  const body = extractBody(error);
  const limit = asNumber(body && body.limit);
  const windowSeconds = asNumber(body && body.windowSeconds);

  let retryAfter = asNumber(body && body.retryAfterSeconds);
  if (isNaN(retryAfter)) {
    const headers = error && (error as any).headers;
    if (headers && typeof headers.get === 'function') {
      const raw = headers.get('Retry-After');
      const parsed = typeof raw === 'string' ? parseInt(raw, 10) : asNumber(raw);
      if (!isNaN(parsed) && isFinite(parsed)) { retryAfter = parsed; }
    }
  }
  // Deliberately NO fallback to windowSeconds -- see the field doc above.

  return {
    limit: limit,
    windowSeconds: windowSeconds,
    retryAfterSeconds: retryAfter,
    message: asString(body && body.error) || asString(body && body.details) || asString(body && body.message)
  };
}

/** True when the server refused because a rate limit was hit. */
export function isRateLimited(error: any): boolean {
  return asNumber(error && (error as any).status) === 429;
}

/**
 * The sentence to put in front of a user.
 *
 * Prefers the server's own words (`details`, or a ResponseMessage `message`)
 * and falls back to the caller's already-translated string. The stable code is
 * never returned on its own -- see the header.
 */
export function describeServerError(error: any, fallback: string): string {
  const info = parseServerError(error);
  return info.details ? info.details : fallback;
}

/**
 * A short, factual line for a support ticket -- never the primary message.
 *
 * ---------------------------------------------------------------------------
 * WHY NOT `error.message`
 * ---------------------------------------------------------------------------
 * Angular builds `HttpErrorResponse.message` itself, in English, naming the
 * URL: *"Http failure response for http://host/1.0/ui/project/mapping/review/
 * 5698: 404 Not Found"*. Several screens were rendering exactly that to
 * whoever was looking -- including customers, in any of the 20 non-English
 * locales -- because it was the only string to hand.
 *
 * House rule (2026-10-23): every string presented to a user is in their
 * language. The exception is data from their own account -- a book title, a
 * store domain -- which is theirs and passes through verbatim. A framework's
 * diagnostic is neither.
 *
 * So callers render their own translated sentence and use this only as a
 * labelled secondary line. It prefers the server's own `details` (which names
 * the actual row and state and is what support will ask for) and otherwise
 * falls back to the bare status.
 *
 * `HTTP 404` is a status code and not a quantity, so it is deliberately NOT
 * localized through `numberFormat`.
 */
export function technicalErrorDetail(error: any): string {
  const info = parseServerError(error);
  if (info.details) { return info.details; }
  return info.httpStatus ? 'HTTP ' + info.httpStatus : '';
}

/**
 * True when re-sending the identical request could plausibly succeed.
 *
 * Retryable: no response at all (status 0 -- offline, DNS, dropped
 * connection, CORS rejection), 408 request timeout, 425 too early, and any
 * 5xx.
 *
 * NOT retryable: every other 4xx. Those are deterministic rejections of THIS
 * request -- a malformed payload, a closed manufacturing set, an expired
 * token, a missing row. The identical request will be rejected identically
 * however many times it is sent, so a retry only delays the error message and
 * multiplies server load. This is the whole point of the module.
 *
 * ⚠ **429 is NOT retryable, despite being temporary.** It used to be, on the
 * reasoning that a rate limit lifts by itself. It does -- but not on any
 * timescale {@link retryTransient} can wait out: the backoff here tops out
 * around two seconds and the windows the server actually uses are rolling
 * *hours* (`ResourceUploadUrlsPerOrgPerHour`, window 3600s). So every retry is
 * guaranteed to be refused, and the server has asked us explicitly not to send
 * them -- "do not retry automatically inside the window ... it will just burn
 * the user's hour"
 * (`docs/contracts/shared/ResourceUploads.md` §3). The correct response to a
 * 429 is to show the user the limit and stop; see {@link parseRateLimit}.
 *
 * A non-HTTP throwable (a TypeError from our own `map`, say) is NOT retryable:
 * re-running a callback that threw on the response body will throw again.
 */
export function isTransientHttpError(error: any): boolean {
  if (!error || typeof error !== 'object') { return false; }
  const status = asNumber((error as any).status);
  if (isNaN(status)) { return false; }
  if (status === 0) { return true; }
  if (status === 408 || status === 425) { return true; }
  return status >= 500 && status < 600;
}

/**
 * Retry only transient failures, with exponential backoff.
 *
 * ⚠ **Retry counts on a POST are a correctness question, not a tuning knob.**
 * A POST is not idempotent. "No response" does not mean "not processed" -- the
 * request may have been committed and only the response lost, so a retry can
 * create a SECOND Order / OrderDeliverTo. `retry(6)` meant that hazard applied
 * six times over on every mutating call in the order path. Hence:
 *
 *   - GET / read paths: retry freely (2-3 is reasonable).
 *   - POST / mutating paths: pass `maxRetries = 1`. One retry recovers the
 *     common case (a single dropped connection or a 503 during a deploy)
 *     while bounding the duplicate-creation window to one extra attempt.
 *
 * The real fix is a server-side idempotency key -- the place-order envelope
 * already has `clientRequestId`, but `POST /orderDeliverTo` and `POST /order`
 * do not. Raised with the server; until it exists, keep the count low rather
 * than raising it because a flaky network was observed.
 */
export function retryTransient<T>(maxRetries: number = 1, baseDelayMs: number = 500): MonoTypeOperatorFunction<T> {
  return (source: Observable<T>): Observable<T> => source.pipe(
    retryWhen(errors => errors.pipe(
      concatMap((error, index) => {
        if (index >= maxRetries || !isTransientHttpError(error)) {
          return throwError(error);
        }
        return timer(baseDelayMs * Math.pow(2, index));
      })
    ))
  );
}

/**
 * True when a body that arrived under a SUCCESS status is actually an error.
 *
 * Defence in depth against the exact defect PF-13 was: a `SmartError` served
 * with 201. The server no longer does this, but the failure mode was silent
 * and expensive (a cleared cart), and the guard costs one property test. A
 * real created row always carries a positive `ID`; a `SmartError` never does.
 */
export function isErrorBody(body: any): boolean {
  if (!body || typeof body !== 'object') { return true; }
  if (typeof body.statusCode === 'number' && body.statusCode >= 400) { return true; }
  if (typeof body.details === 'string' && !('ID' in body)) { return true; }
  return false;
}

/**
 * Build the `Error` that services throw so every caller sees one shape.
 *
 * `fallbackMessage` should already be translated by the caller
 * (`LanguagePipe.get(...)`); it is only used when the server did not supply a
 * sentence of its own. Server `details` are English and are shown as-is --
 * they name specific rows and states, so a mistranslation would be worse than
 * English.
 */
export function toServerCallError(error: any, operation: string, fallbackMessage: string): ServerCallError {
  const info = parseServerError(error);
  const err = new Error(info.details || fallbackMessage) as ServerCallError;
  err.name = 'ServerCallError';
  err.operation = operation;
  err.httpStatus = info.httpStatus;
  err.statusCode = info.statusCode;
  err.code = info.code;
  err.details = info.details;
  err.body = info.body;
  err.raw = info.raw;
  return err;
}
